import _ from "underscore";
import storage from "../../utils/storage/storage";
import { MobileActions } from "../../store/mobile/actions";
import pageHTML from "./localStorage.tmpl";
import DebugPageView from "./debugPageView";
import { _originalLocalStorage } from "../../utils/storage/storage.base";
import { IndexedDbStorage } from "../../utils/storage/indexedDbStorage";
import jsonTreeView from "../../utils/jsonTreeView";

function sortObjectKeys(value: unknown): unknown {
    if (Array.isArray(value)) {
        return value.map(sortObjectKeys);
    }
    if (value !== null && typeof value === "object") {
        const obj = value as Record<string, unknown>;
        const sorted: Record<string, unknown> = {};
        Object.keys(obj).sort().forEach(k => {
            sorted[k] = sortObjectKeys(obj[k]);
        });
        return sorted;
    }
    return value;
}

function buildEntry(key: string, value: unknown): HTMLElement {
    const entry = document.createElement("div");
    entry.className = "debug-localstorage__entry";

    const keyEl = document.createElement("div");
    keyEl.className = "debug-localstorage__key";
    keyEl.textContent = key;
    entry.appendChild(keyEl);

    const treeEl = document.createElement("div");
    treeEl.className = "debug-localstorage__tree";
    entry.appendChild(treeEl);

    if (value === null || typeof value !== "object") {
        treeEl.textContent = String(value ?? "");
    } else {
        const sorted = sortObjectKeys(value as Record<string, unknown>);
        jsonTreeView.renderJSON(JSON.stringify(sorted), treeEl);
    }

    return entry;
}

function isDriveKey(key: string): boolean {
    return key.includes("geotabDrive") || key.includes("geotabAPI");
}

export default class LocalStorageView extends DebugPageView {
    private readonly template = _.template(pageHTML);

    render() {
        this.el.innerHTML = this.template({
            storageUsed: storage.size().toFixed(2)
        });

        this.app.store.dispatch(MobileActions.setPageState({
            title: "Data Storage",
            pageLevel: 2
        }));

        this.renderLocalStorageKeys();
        this.renderIndexedDbKeys();
        return this;
    }

    private renderLocalStorageKeys() {
        const container = this.el.querySelector("#debug-localstorage__ls-keys") as HTMLElement | null;
        if (!container) {
            return;
        }

        const keys: string[] = [];
        for (let i = 0; i < _originalLocalStorage.length; i++) {
            const key = _originalLocalStorage.key(i) ?? "";
            if (isDriveKey(key)) {
                keys.push(key);
            }
        }
        keys.sort();

        for (const key of keys) {
            const raw = _originalLocalStorage.getItem(key);
            let value: unknown;
            try {
                value = JSON.parse(raw ?? "");
            } catch {
                value = raw;
            }
            container.appendChild(buildEntry(key, value));
        }
    }

    private renderIndexedDbKeys() {
        const container = this.el.querySelector("#debug-localstorage__idb-keys") as HTMLElement | null;
        const usageEl = this.el.querySelector("#debug-idb-usage") as HTMLElement | null;
        if (!container) {
            return;
        }

        Promise.all([
            IndexedDbStorage.getEverythingFromIndexedDb(),
            IndexedDbStorage.size()
        ]).then(([data, sizeKb]) => {
            if (usageEl) {
                usageEl.textContent = `${ sizeKb.toFixed(2) } KB`;
            }
            const keys = Object.keys(data).filter(isDriveKey).sort();

            if (keys.length === 0) {
                const empty = document.createElement("div");
                empty.className = "debug-localstorage__empty";
                empty.textContent = "No data";
                container.appendChild(empty);
                return;
            }

            for (const key of keys) {
                // IDB values are structured objects; handle string-serialized values too
                let value: unknown = (data as Record<string, unknown>)[key];
                if (typeof value === "string") {
                    try {
                        value = JSON.parse(value);
                    } catch { /* leave as string */ }
                }
                container.appendChild(buildEntry(key, value));
            }
        }).catch(() => {
            const err = document.createElement("div");
            err.className = "debug-localstorage__empty";
            err.textContent = "Failed to load IndexedDb";
            container.appendChild(err);
        });
    }
}
